import { collection, getDocs, updateDoc, doc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { getUnsplashPhotoById } from '../lib/unsplash';

/**
 * Extract photo ID from Unsplash URL
 * @param {string} url - Unsplash image URL
 * @returns {string|null} Photo ID or null
 */
function extractUnsplashPhotoId(url) {
  // Extract photo ID from Unsplash URL
  // Example: https://images.unsplash.com/photo-1234567890?... -> 1234567890
  const match = url.match(/photo-([a-zA-Z0-9_-]+)/);
  return match ? match[1] : null;
}

/**
 * Migration script to fetch full Unsplash photo data for existing images
 * Run this ONCE as admin to update existing content
 */
export async function migrateUnsplashData() {
  console.log('Starting Unsplash data migration...');
  
  const contentSnapshot = await getDocs(collection(db, 'content'));
  let updated = 0;
  let skipped = 0;
  let errors = 0;
  
  for (const contentDoc of contentSnapshot.docs) {
    const data = contentDoc.data();
    
    // Skip if already has unsplashPhoto
    if (data.unsplashPhoto) {
      console.log(`⊘ Skipped ${contentDoc.id}: Already has unsplashPhoto`);
      skipped++;
      continue;
    }
    
    // Skip if no image or not from Unsplash
    if (!data.image || !data.image.includes('unsplash.com')) {
      console.log(`⊘ Skipped ${contentDoc.id}: No Unsplash image`);
      skipped++;
      continue;
    }
    
    try {
      // Extract photo ID from URL
      const photoId = extractUnsplashPhotoId(data.image);
      
      if (!photoId) {
        console.log(`⊘ Skipped ${contentDoc.id}: Could not extract photo ID from: ${data.image}`);
        skipped++;
        continue;
      }
      
      console.log(`Fetching photo data for ${contentDoc.id} (photo ID: ${photoId})...`);
      
      // Fetch full photo data from Unsplash API
      const photo = await getUnsplashPhotoById(photoId);
      
      if (photo) {
        // Update Firestore document with full photo object
        await updateDoc(doc(db, 'content', contentDoc.id), {
          unsplashPhoto: photo
        });
        
        console.log(`✅ Updated ${contentDoc.id}: ${data.title}`);
        updated++;
      } else {
        console.log(`⊘ Skipped ${contentDoc.id}: Could not fetch photo data`);
        skipped++;
      }
      
      // Rate limit: wait 100ms between requests
      await new Promise(resolve => setTimeout(resolve, 100));
      
    } catch (error) {
      console.error(`❌ Error updating ${contentDoc.id}:`, error);
      errors++;
    }
  }
  
  console.log(`\n✅ Migration complete!`);
  console.log(`   Updated: ${updated}`);
  console.log(`   Skipped: ${skipped}`);
  console.log(`   Errors: ${errors}`);
  
  return { updated, skipped, errors };
}

// Helper function to run migration from browser console
// Usage: window.migrateUnsplashData()
if (typeof window !== 'undefined') {
  window.migrateUnsplashData = migrateUnsplashData;
}

